import {NgModule} from '@angular/core';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {CoreModule} from '../../../../../core/core.module';
import {DmModule} from '../../../../../feature-core/dm/dm.module';

import {CwRequestCapturePage} from '../../../page/object/cw-request-capture-page/cw-request-capture.page';
import {CwRequestMinePage} from '../../../page/object/cw-request-mine-page/cw-request-mine.page';
import {CwRequestReviewPage} from '../../../page/object/cw-request-review-page/cw-request-review.page';

import {CwRequestDetailPanelComponent} from '../cw-request-detail-panel/cw-request-detail-panel.component';
import {CwRequestDocumentListComponent} from '../cw-request-detail-panel/cw-request-document-list.component';
import {CwRequestProposalPanelComponent} from '../cw-request-detail-panel/cw-request-proposal-panel.component';
import {CwRequestCommentTrailComponent} from '../cw-request-detail-panel/cw-request-comment-trail.component';
import {
    CwRequestObjectComponent
} from '../cw-request-object-component/cw-request-object.component';
import {
    CwRequestContextHeaderComponent
} from '../cw-request-object-component/component/cw-request-context-header/cw-request-context-header.component';
import {
    CwRequestInlineComponent
} from '../cw-request-object-component/component/cw-request-inline-component/cw-request-inline.component';
// [cw_qualitaetscenter] Rumpf der Antragseingangs-Werkbank. Liegt im page/-Zweig des
// Qualitätscenters, wird aber HIER deklariert — Begründung im exports-Block unten.
import {
    CwRequestReviewWorkbenchComponent
} from '../../../page/dashboard/cw-case-dashboard/components/cw-request-review-workbench/cw-request-review-workbench.component';
import {CW_REQUEST_ENTITY_PROVIDER} from './cw-request.entity-provider';

/**
 * Feature module of the Antragseingang (T3) — declares and exports every page and component of
 * the request intake.
 *
 * <h3>Why this module exists at all</h3>
 * The cw module has no sub-module tradition; everything is declared in the single
 * {@code CwModule}. This one is a DELIBERATE exception, made for two reasons:
 *
 * 1. {@code cw.module.ts} is owned by the FE registration agent (Konsolidierung §3 K9 / §5 F0)
 *    and is written ONCE for all four parallel themes. Bundling the T3 declarations here shrinks
 *    the required foreign edit from roughly a dozen declaration/export lines — every one of them a
 *    merge hazard against the three other themes — to a SINGLE import line.
 * 2. The consultant shell needs exactly these components and nothing else of {@code CwModule}.
 *    Importing this module there keeps the shell self-sufficient instead of pulling in the whole
 *    cw surface.
 *
 * A component may only be declared in ONE NgModule: once {@code CwModule} imports this module, it
 * must NOT also declare these classes.
 *
 * {@code CoreModule} carries SharedModule (mvs-widget, mvs-review-workbench, mvs-pdf-viewer,
 * mvs-crud-object-generic, the PrimeNG surface); {@code DmModule} carries {@code mvs-dm-upload}
 * AND {@code mvs-dm-object-documents} (both are declared AND exported by it), which is what the
 * capture page's document column is built from.
 *
 * {@code ConfirmDialogModule} is imported EXPLICITLY: {@code SharedModule} provides the
 * {@code ConfirmationService} but does not export {@code p-confirmDialog} (CwModule imports the
 * module for the same reason). The Storno safeguard of {@code CwRequestContextHeaderComponent}
 * needs the component, and without this import it would silently render as an unknown element.
 *
 * <h3>The module carries its own registry entries</h3>
 * Declaring the pages is only half of what a shell needs. Every metadata-driven form and widget of
 * this surface resolves its CRUD service through the {@code ENTITY_SERVICE_REGISTRY}, and that
 * registry was provided ONLY by {@code CwModule} — which the consultant shell deliberately does not
 * import. The consultant shell therefore had the routes and the components but not a single
 * {@code cw.*} alias, so its request pages rendered SILENTLY EMPTY. Providing
 * {@code CW_REQUEST_ENTITY_PROVIDER} here makes the module self-sufficient: whoever imports it gets
 * the pages AND the aliases they need.
 */
@NgModule({
    imports: [
        CoreModule,
        DmModule,
        ConfirmDialogModule,
    ],
    declarations: [
        // Pages
        CwRequestCapturePage,
        CwRequestMinePage,
        CwRequestReviewPage,
        // Review workbench detail surface. CwRequestDocumentListComponent belongs to the BACK
        // OFFICE only: it is the one list that renders the document role as a correctable dropdown,
        // the read state with its confidence and the read error of a failed run. The capture page
        // used to stack it under its upload zone as well and no longer does — role, read state and
        // confidence come into existence during processing, so on the Berater's surface the generic
        // mvs-dm-object-documents is the right list.
        CwRequestDetailPanelComponent,
        CwRequestDocumentListComponent,
        CwRequestProposalPanelComponent,
        CwRequestCommentTrailComponent,
        // Object component family of cw.CwRequest (shell + its two presentations). The shell is
        // referenced by CW_REQUEST_ENTITY_ENTRIES, so it must be declared by the SAME module that
        // provides those entries — otherwise a shell resolves the alias to a component it cannot
        // instantiate.
        CwRequestObjectComponent,
        CwRequestContextHeaderComponent,
        CwRequestInlineComponent,
        // [cw_qualitaetscenter] Der Werkbank-Rumpf, den CwRequestReviewPage (oben, von DIESEM Modul
        // deklariert) in seinem Template mountet. Er MUSS deshalb hier deklariert sein.
        CwRequestReviewWorkbenchComponent,
    ],
    exports: [
        CwRequestCapturePage,
        CwRequestMinePage,
        CwRequestReviewPage,
        CwRequestDetailPanelComponent,
        CwRequestDocumentListComponent,
        CwRequestProposalPanelComponent,
        CwRequestCommentTrailComponent,
        CwRequestObjectComponent,
        CwRequestContextHeaderComponent,
        CwRequestInlineComponent,
        // [cw_qualitaetscenter] The workbench today has exactly ONE mount point:
        // CwRequestReviewPage (cw/request/review), declared by THIS module, which the declaration
        // above already covers. Its former second mount point, the „Antragseingang" section of
        // CwCaseDashboardPage (cw/dashboard?navItem=requests, declared by CwModule), was removed on
        // 2026-09-16 — that host was the reason the export used to be mandatory.
        // The export is KEPT deliberately: CwModule imports CwRequestModule (not the other way round —
        // that direction would be a cycle), so the export is the only route by which a host declared
        // in CwModule can mount the workbench again without a module change. The declaration must
        // stay HERE and not move to CwModule: the consultant shell imports CwRequestModule DIRECTLY
        // and never CwModule (app.module.ts:31/84) — cw/request/review would otherwise hit an unknown
        // element there.
        CwRequestReviewWorkbenchComponent,
    ],
    providers: [
        CW_REQUEST_ENTITY_PROVIDER,
    ],
})
export class CwRequestModule {
}
